/**
 * Le document de texte riche, côté client (docs/11 §3).
 *
 * Le format est **ProseMirror / TipTap**, et c'est le contrat qui compte : le
 * serveur valide ce document contre une allowlist de nœuds
 * (`teach/shared/richtext.py`), et le rendu mappe des nœuds typés vers des
 * composants React. Aucun `dangerouslySetInnerHTML` n'existe dans ce code, ce
 * qui rend le stockage immunisé contre le XSS stocké.
 *
 * ## Pourquoi ce fichier est dans `shared/`
 *
 * Il est né dans `features/catalog/lib/content.ts`, pour le corps d'une leçon.
 * Le jour où la description d'un cours, la biographie d'un profil et
 * l'appréciation d'une copie sont devenues des documents, il a cessé
 * d'appartenir à son premier propriétaire : à partir de deux, `shared/`.
 */

/**
 * Un nœud du document, tel que le serveur l'accepte.
 *
 * Des **alias de type** et non des interfaces, et ce n'est pas indifférent :
 * TypeScript donne un index de chaîne implicite à un alias, pas à une
 * interface. C'est ce qui rend un document assignable au
 * `Record<string, unknown>` par lequel le contrat OpenAPI décrit un JSONB — et
 * qui évite un `{ ...doc }` recopié à chaque appel de mutation.
 */
export type ContentNode = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: ContentNode[];
  text?: string;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
};

export type ContentDocument = {
  type: "doc";
  content: ContentNode[];
};

export const EMPTY_DOCUMENT: ContentDocument = { type: "doc", content: [] };

/**
 * Nœuds qui **montrent** quelque chose sans porter de texte.
 *
 * Le pendant de `_SUBSTANTIVE_NODES` côté serveur, et pour la même raison : un
 * tableau qu'on vient de dessiner, une ressource intégrée ou un séparateur ne
 * sont pas un champ laissé vide — or `toStored` rend `null` pour ce qui est
 * vide. Sans cette liste, un auteur qui pose un tableau et enregistre avant de
 * le remplir le retrouve effacé.
 */
const SUBSTANTIVE_NODES = ["resourceEmbed", "table", "horizontalRule"];

/**
 * Le document est-il vide **au sens de l'auteur** ?
 *
 * TipTap laisse toujours un paragraphe derrière lui quand on efface le texte :
 * juger sur `content.length` ferait passer pour rempli un champ que
 * l'utilisateur vient de vider. Le serveur applique exactement la même règle
 * (`is_empty`) — texte extrait vide **et** aucun nœud qui se voie sans texte.
 */
export function isEmptyDocument(document: ContentDocument | null | undefined): boolean {
  if (extractText(document).trim() !== "") {
    return false;
  }
  return !(document?.content ?? []).some(carriesSubstantiveNode);
}

function carriesSubstantiveNode(node: ContentNode): boolean {
  return SUBSTANTIVE_NODES.includes(node.type) || (node.content ?? []).some(carriesSubstantiveNode);
}

/**
 * Texte brut d'un document.
 *
 * Le pendant de `extract_text` côté serveur, à ceci près qu'il ne sert ici qu'à
 * décider du vide et à donner un aperçu d'une ligne. Ce qui compte pour la
 * recherche est calculé **côté serveur**, sur le document reçu.
 */
export function extractText(document: ContentDocument | null | undefined): string {
  return (document?.content ?? []).map(nodeText).join("\n").trim();
}

function nodeText(node: ContentNode): string {
  if (node.type === "text") {
    return node.text ?? "";
  }
  return (node.content ?? []).map(nodeText).join("");
}

/**
 * Lit un `content` venu de l'API.
 *
 * Le contrat OpenAPI le type `Record<string, unknown>` — un JSONB n'a pas de
 * forme déclarée — d'où cette porte d'entrée unique qui le resserre. La
 * validation, elle, a déjà eu lieu **côté serveur** : ce document ne peut pas
 * contenir de nœud hors allowlist, et c'est ce qui rend le rendu sûr.
 */
export function asDocument(raw: Record<string, unknown> | null | undefined): ContentDocument {
  if (
    raw === null ||
    raw === undefined ||
    raw["type"] !== "doc" ||
    !Array.isArray(raw["content"])
  ) {
    return EMPTY_DOCUMENT;
  }
  return { type: "doc", content: raw["content"] as ContentNode[] };
}

/**
 * Ce qui part vers le serveur : le document, ou `null` s'il est vide.
 *
 * `null` et non le document vide, parce que c'est ainsi que le domaine range
 * « pas de description » — un champ vidé qui reviendrait en
 * `{"type":"doc","content":[]}` donnerait deux représentations de la même
 * absence, et chaque lecteur devrait penser aux deux.
 */
export function toStored(document: ContentDocument | null | undefined): ContentDocument | null {
  if (document === null || document === undefined) {
    return null;
  }
  const stripped = { type: "doc" as const, content: document.content.map(canonical) };
  return isEmptyDocument(stripped) ? null : stripped;
}

/**
 * Un document de paragraphes, à partir de texte brut.
 *
 * Le miroir de `document_from_text` côté serveur. Sert là où l'on tient du
 * texte et pas d'éditeur : les jeux d'essai, et tout écran qui aurait à
 * présenter une chaîne dans un rendu de document.
 */
export function documentFromText(text: string): ContentDocument {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  return {
    type: "doc",
    content: lines.map((line) => ({
      type: "paragraph",
      content: [{ type: "text", text: line }],
    })),
  };
}

/**
 * La forme **canonique** d'un nœud, celle que le serveur garde.
 *
 * TipTap sérialise tout attribut déclaré, y compris ceux qu'on n'a pas posés :
 * un paragraphe sans alignement sort en `{"textAlign": null}`. Le serveur
 * retire ces nuls (`_as_attr_mapping`) ; les envoyer quand même ferait que le
 * document relu ne serait jamais celui qu'on vient d'envoyer, et un éditeur qui
 * se croit « modifié » dès l'ouverture demande d'enregistrer une page à
 * laquelle personne n'a touché.
 *
 * Les deux moitiés de cette règle sont donc écrites dans deux langages, et
 * c'est un test qui les tient d'accord : `richtext-sample.json`, que les deux
 * suites relisent.
 */
function canonical(node: ContentNode): ContentNode {
  const cleaned: ContentNode = { type: node.type };
  const attrs = withoutNulls(node.attrs);
  if (attrs !== undefined) {
    cleaned.attrs = attrs;
  }
  if (node.text !== undefined) {
    cleaned.text = node.text;
  }
  if (node.marks !== undefined && node.marks.length > 0) {
    cleaned.marks = node.marks.map((mark) => {
      const markAttrs = withoutNulls(mark.attrs);
      return markAttrs === undefined ? { type: mark.type } : { type: mark.type, attrs: markAttrs };
    });
  }
  if (node.content !== undefined && node.content.length > 0) {
    cleaned.content = node.content.map(canonical);
  }
  return cleaned;
}

function withoutNulls(
  attrs: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (attrs === undefined) {
    return undefined;
  }
  const kept = Object.entries(attrs).filter(([, value]) => value !== null && value !== undefined);
  return kept.length === 0 ? undefined : Object.fromEntries(kept);
}
